import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagic, faRobot, faBrain } from '@fortawesome/free-solid-svg-icons';
import { Color } from 'core';
import styled, { keyframes, css } from 'styled-components';
import LoadingDots from 'components/common/LoadingDots';

interface IBuildingCourse {
  course: any;
}

const BuildingCourse = (props: IBuildingCourse) => {
  return (
    <Container>
      <ContentWrapper>
        <Header>
          <IconGroup>
            <MainIcon>
              <FontAwesomeIcon icon={faMagic} />
            </MainIcon>
            <FloatingIcon delay="0s">
              <FontAwesomeIcon icon={faBrain} />
            </FloatingIcon>
            <FloatingIcon delay="1s">
              <FontAwesomeIcon icon={faRobot} />
            </FloatingIcon>
          </IconGroup>
          <Title>Creating Your Course</Title>
          <Subtitle>"{props.course.name}"</Subtitle>
        </Header>

        <ThinkingSection>
          <LoadingDots />
          <ThinkingText>AI is working its magic...</ThinkingText>
        </ThinkingSection>

        <InfoBox>
          <InfoTitle>What's happening behind the scenes?</InfoTitle>
          <InfoList>
            <InfoItem>🧠 Analyzing your topic and difficulty level</InfoItem>
            <InfoItem>📚 Researching and curating relevant content</InfoItem>
            <InfoItem>🎯 Breaking content down into focused assignments</InfoItem>
            <InfoItem>✨ Creating instructional content and quiz items</InfoItem>
          </InfoList>
        </InfoBox>
      </ContentWrapper>
    </Container>
  );
};

export default BuildingCourse;

// Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;


const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

// Styled Components
const Container = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border-radius: 16px;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  text-align: center;
  ${css`
    animation: ${fadeInUp} 0.6s ease-out;
  `}
`;

const Header = styled.div`
  margin-bottom: 48px;
`;

const IconGroup = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 120px;
  width: 200px;
  margin: 0 auto 24px;
`;

const MainIcon = styled.div`
  font-size: 64px;
  color: ${Color.primaryBlue};
  ${css`
    animation: ${pulse} 2s infinite;
  `}
  z-index: 2;
`;

const FloatingIcon = styled.div<{ delay: string }>`
  position: absolute;
  font-size: 24px;
  color: ${Color.primaryBlue};
  opacity: 0.6;
  ${css`
    animation: ${float} 3s infinite;
  `}
  animation-delay: ${(props) => props.delay};

  &:nth-child(2) {
    left: -70px;
    top: 40px;
  }

  &:nth-child(3) {
    right: -70px;
    top: 40px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${Color.primaryBlack};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.h2`
  font-size: 20px;
  font-weight: 400;
  color: ${Color.darkGray};
  margin: 0;
  font-style: italic;
`;

const ThinkingSection = styled.div`
  margin: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;


const ThinkingText = styled.p`
  font-size: 18px;
  color: ${Color.primaryBlack};
  margin: 0;
  min-height: 22px;
  text-align: center;
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  margin-top: 32px;
  text-align: left;
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${Color.primaryBlack};
  margin: 0 0 16px 0;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.li`
  font-size: 14px;
  color: ${Color.darkGray};
  line-height: 1.4;
`;
